import classNames from "classnames";
import * as React from "react";
import KuikaDrawnCheck from "../kuika-drawn-check/kuika-drawn-check";
import KuikaSmallMediumText from "../typography/SmallMediumText/SmallMediumText";
import "./kuika-stepper.scss";

export interface KuikaStepperStep {
  /** Stable key, also used to build the step's `data-testid`. */
  key: string;
  title: string;
  /** Shown under the title. Omit for a single-line step. */
  description?: string;
  /** Blocks navigation to this step and mutes it. */
  disabled?: boolean;
}

export interface KuikaStepperProps {
  steps: KuikaStepperStep[];
  /** Zero-based index of the step being edited. */
  current: number;
  onChange?: (index: number, step: KuikaStepperStep) => void;
  /** Set false for a wizard whose later steps are not reachable until completed. */
  allowJumpAhead?: boolean;
  className?: string;
  dataTestId?: string;
}

type KuikaStepperState = "done" | "current" | "upcoming";

/** The last move of `current`, kept until the next one so a re-render never cuts its animation. */
interface IStepperMove {
  from: number;
  to: number;
}

interface IStepStyle extends React.CSSProperties {
  "--step-order"?: number;
}

/**
 * A linear wizard progress control: one numbered node per step, joined by a rail
 * that fills up to the current step.
 *
 * This is not a tab strip. Tabs are peers the user switches between; steps are an
 * ordered sequence with completion behind them and work ahead — which is why the
 * node carries a check once passed, and why a step can be closed off entirely.
 *
 * Moving forward plays the fill in order — the rail fills, the next node fills, the passed node
 * draws its check and pops — staggered across every step a jump passes. Moving back is instant,
 * and so is the first render.
 */
const KuikaStepper: React.FC<KuikaStepperProps> = ({
  steps,
  current,
  onChange,
  allowJumpAhead = true,
  className,
  dataTestId
}: KuikaStepperProps) => {
  // Derived from the previous prop during render (React's documented pattern), so the move lives
  // exactly as long as this `current`.
  const [previousCurrent, setPreviousCurrent] = React.useState<number>(current);
  const [move, setMove] = React.useState<IStepperMove | null>(null);
  if (current !== previousCurrent) {
    setPreviousCurrent(current);
    setMove({ from: previousCurrent, to: current });
  }
  const isForward: boolean = move !== null && move.to > move.from;
  let direction: "forward" | "back" | undefined;
  if (move !== null) {
    direction = isForward ? "forward" : "back";
  }

  const resolveState = (index: number): KuikaStepperState => {
    if (index < current) return "done";
    if (index === current) return "current";
    return "upcoming";
  };

  const isReachable = (index: number, step: KuikaStepperStep): boolean => {
    if (step.disabled === true) return false;
    if (index <= current) return true;
    return allowJumpAhead;
  };

  return (
    <ol className={classNames("kuika-stepper", className)} data-testid={dataTestId} data-direction={direction}>
      {steps.map((step: KuikaStepperStep, index: number) => {
        const state: KuikaStepperState = resolveState(index);
        const reachable: boolean = isReachable(index, step);
        const isLast: boolean = index === steps.length - 1;
        // Every step the forward move touched, numbered from where it started: its place in the fill.
        const isAdvancing: boolean = isForward && move !== null && index >= move.from && index <= move.to;
        const stepStyle: IStepStyle | undefined =
          isAdvancing && move !== null ? { "--step-order": index - move.from } : undefined;
        return (
          <li
            key={step.key}
            className={classNames("kuika-stepper__step", `kuika-stepper__step--${state}`, {
              "kuika-stepper__step--last": isLast,
              "kuika-stepper__step--unreachable": !reachable,
              "kuika-stepper__step--advancing": isAdvancing
            })}
            data-state={state}
            style={stepStyle}
          >
            <button
              type="button"
              className="kuika-stepper__trigger"
              data-testid={`stepperStep-${step.key}`}
              aria-current={state === "current" ? "step" : undefined}
              disabled={!reachable}
              onClick={(): void => onChange?.(index, step)}
            >
              <span className="kuika-stepper__node" aria-hidden="true">
                {state === "done" ? <KuikaDrawnCheck className="kuika-stepper__check" draw={isAdvancing} /> : index + 1}
              </span>
              <span className="kuika-stepper__text">
                <KuikaSmallMediumText className="kuika-stepper__title">{step.title}</KuikaSmallMediumText>
                {step.description && (
                  <KuikaSmallMediumText className="kuika-stepper__description">{step.description}</KuikaSmallMediumText>
                )}
              </span>
            </button>
            {!isLast && (
              <span className="kuika-stepper__rail" aria-hidden="true">
                <span className="kuika-stepper__rail-fill" />
              </span>
            )}
          </li>
        );
      })}
    </ol>
  );
};

export default KuikaStepper;
